import {
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfWeek,
  addDays,
  setDay,
  addWeeks,
  isBefore
} from 'date-fns';

export interface Holiday {
  title: string;
  date: Date;
  emoji?: string;
}

export interface HolidayRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Calculate Thanksgiving (4th Thursday of November)
 */
export const getThanksgivingDate = (year: number): Date => {
  const november = new Date(year, 10, 1); // November 1st
  const dayOfWeek = november.getDay();
  // Find first Thursday: (4 - dayOfWeek + 7) % 7 gives days until Thursday
  const firstThursday = ((4 - dayOfWeek + 7) % 7) + 1;
  // 4th Thursday = first Thursday + 21 days
  return new Date(year, 10, firstThursday + 21);
};

/**
 * Get Christmas Day for a given year
 */
export const getChristmasDate = (year: number): Date => {
  return new Date(year, 11, 25); // December 25
};

/**
 * Get New Year's Day for a given year
 */
export const getNewYearsDate = (year: number): Date => {
  return new Date(year, 0, 1); // January 1
};

/**
 * Get 4th of July for a given year
 */
export const getFourthOfJulyDate = (year: number): Date => {
  return new Date(year, 6, 4); // July 4
};

/**
 * Get Martin Luther King Jr. Day (3rd Monday in January)
 */
export const getMartinLutherKingDayDate = (year: number): Date => {
  const january = new Date(year, 0, 1); // January 1st
  const dayOfWeek = january.getDay();
  // Find first Monday: (1 - dayOfWeek + 7) % 7 gives days until Monday
  const firstMonday = ((1 - dayOfWeek + 7) % 7) + 1;
  // 3rd Monday = first Monday + 14 days
  return new Date(year, 0, firstMonday + 14);
};

/**
 * Get Presidents' Day (3rd Monday in February)
 */
export const getPresidentsDayDate = (year: number): Date => {
  const february = new Date(year, 1, 1); // February 1st
  const dayOfWeek = february.getDay();
  // Find first Monday: (1 - dayOfWeek + 7) % 7 gives days until Monday
  const firstMonday = ((1 - dayOfWeek + 7) % 7) + 1;
  // 3rd Monday = first Monday + 14 days
  return new Date(year, 1, firstMonday + 14);
};

/**
 * Get Memorial Day (last Monday in May)
 */
export const getMemorialDayDate = (year: number): Date => {
  const may = new Date(year, 4, 1); // May 1st
  const dayOfWeek = may.getDay();
  // Find first Monday: (1 - dayOfWeek + 7) % 7 gives days until Monday
  const firstMonday = ((1 - dayOfWeek + 7) % 7) + 1;
  // Last Monday = first Monday + 21 days (5th Monday, which is always the last in May)
  // Note: May has 31 days, so the 5th Monday (1st + 21) falls between May 29-31
  return new Date(year, 4, firstMonday + 21);
};

/**
 * Get all major US holidays for a given year
 */
export const getHolidaysForYear = (year: number): Holiday[] => {
  return [
    {
      title: 'Thanksgiving Day',
      date: getThanksgivingDate(year),
      emoji: '🦃'
    },
    {
      title: 'Christmas Day',
      date: getChristmasDate(year),
      emoji: '🎄'
    },
    {
      title: "New Year's Day",
      date: getNewYearsDate(year),
      emoji: '🎉'
    },
    {
      title: '4th of July',
      date: getFourthOfJulyDate(year),
      emoji: '🎆'
    }
  ];
};

/**
 * Get holidays for a year range (inclusive)
 */
export const getHolidaysForYearRange = (startYear: number, endYear: number): Holiday[] => {
  const holidays: Holiday[] = [];

  for (let year = startYear; year <= endYear; year++) {
    holidays.push(...getHolidaysForYear(year));
  }

  return holidays;
};

/**
 * Get Thanksgiving week range (Monday before to Sunday after)
 */
export const getThanksgivingWeek = (year: number): HolidayRange => {
  const thanksgiving = getThanksgivingDate(year);
  const startDate = addDays(thanksgiving, -3); // Monday before
  const endDate = addDays(thanksgiving, 3); // Sunday after
  return { startDate, endDate };
};

/**
 * Get Martin Luther King Jr. Day weekend range (Saturday before to Monday)
 */
export const getMartinLutherKingDayWeekend = (year: number): HolidayRange => {
  const mlkDay = getMartinLutherKingDayDate(year);
  const startDate = addDays(mlkDay, -2); // Saturday before
  const endDate = mlkDay; // Monday (the holiday)
  return { startDate, endDate };
};

/**
 * Get Presidents' Day weekend range (Saturday before to Monday)
 */
export const getPresidentsDayWeekend = (year: number): HolidayRange => {
  const presidentsDay = getPresidentsDayDate(year);
  const startDate = addDays(presidentsDay, -2); // Saturday before
  const endDate = presidentsDay; // Monday (the holiday)
  return { startDate, endDate };
};

/**
 * Get Winter Break range (2 weeks starting Monday before Christmas)
 * Typically Monday before Dec 25th through the following Monday (2 weeks)
 */
export const getWinterBreak = (year: number): HolidayRange => {
  const christmas = getChristmasDate(year);

  // Find first Monday before Dec 25th
  const startDate = startOfWeek(christmas, { weekStartsOn: 1 }); // Monday of Christmas week

  // 2 weeks from start Monday
  const endDate = addWeeks(startDate, 2);

  return { startDate, endDate };
};

/**
 * Get Spring Break range (entire month of March)
 * Uses current or next year depending on today's date
 */
export const getSpringBreak = (referenceDate: Date = new Date()): HolidayRange => {
  const currentYear = referenceDate.getFullYear();
  const marchThisYear = new Date(currentYear, 2, 1); // March 1st this year
  const marchNextYear = new Date(currentYear + 1, 2, 1); // March 1st next year

  // Use current year if we're before March, otherwise next year
  const targetMarch = isBefore(referenceDate, marchThisYear) ? marchThisYear : marchNextYear;

  const startDate = startOfMonth(targetMarch); // March 1st
  const endDate = endOfMonth(targetMarch); // March 31st

  return { startDate, endDate };
};

/**
 * Get Summer Break range (from Memorial Day to end of third week of August)
 * Uses current or next year depending on today's date
 */
export const getSummerBreak = (referenceDate: Date = new Date()): HolidayRange => {
  const currentYear = referenceDate.getFullYear();
  const memorialDayThisYear = getMemorialDayDate(currentYear);

  // Use current year if we're before Memorial Day, otherwise next year
  const targetYear = isBefore(referenceDate, memorialDayThisYear) ? currentYear : currentYear + 1;

  const startDate = getMemorialDayDate(targetYear); // Memorial Day (last Monday in May)

  // End of third week of August = August 1st + 20 days (to get to ~August 21)
  const augustFirst = new Date(targetYear, 7, 1); // August 1st
  const endDate = addDays(augustFirst, 20); // Approximately August 21st

  return { startDate, endDate };
};

/**
 * Get next month range
 */
export const getNextMonth = (referenceDate: Date = new Date()): HolidayRange => {
  const nextMonthStart = startOfMonth(addMonths(referenceDate, 1));
  const nextMonthEnd = endOfMonth(addMonths(referenceDate, 1));
  return { startDate: nextMonthStart, endDate: nextMonthEnd };
};

/**
 * Get holiday range by preset name
 */
export const getHolidayRange = (
  preset: 'next-month' | 'thanksgiving' | 'winter-break' | 'spring-break' | 'summer-break',
  referenceDate: Date = new Date()
): HolidayRange => {
  const currentYear = referenceDate.getFullYear();

  switch (preset) {
    case 'next-month':
      return getNextMonth(referenceDate);
    case 'thanksgiving':
      return getThanksgivingWeek(currentYear);
    case 'winter-break':
      return getWinterBreak(currentYear);
    case 'spring-break':
      return getSpringBreak(referenceDate);
    case 'summer-break':
      return getSummerBreak(referenceDate);      
  }
};
